import {
  AutoSizer,
  Column,
  Table as VTable,
  SortDirection,
  TableProps as VTableProps,
  Index,
} from "react-virtualized";

import { RowRenderer } from "./RowRenderer/RowRenderer";
import { HeaderRowRenderer } from "./HeaderRowRenderer/HeaderRowRenderer";
import GlobalStyle from "./GlobalStyle";
import { RefObject, ReactNode, createRef, Component } from "react";

type Param<Row> = { rowData: Row };

interface InternalTableProps<Row> {
  getRowKey?: (row: Row) => string | number;
  selectedRows: Row[];
  onSelect?: (rows: Row[]) => void;
  expandedRows?: Row[];
  onExpand?: (rows: Row[]) => void;
  expandedRowHeight: number;
  expandableContent?: (data: Param<Row>) => ReactNode;
  isSelectedAll?: boolean;
  data: Row[];

  children?: ReactNode;
  rowHeight: VTableProps["rowHeight"];
  headerHeight: VTableProps["headerHeight"];
  virtualizedProps?: Omit<
    VTableProps,
    | "ref"
    | "width"
    | "height"
    | "rowHeight"
    | "rowCount"
    | "rowGetter"
    | "rowRenderer"
    | "headerHeight"
    | "headerRowRenderer"
  >;
}

export type TableProps<Row> = JSX.LibraryManagedAttributes<
  typeof Table,
  InternalTableProps<Row>
>;

export class Table<T = any> extends Component<InternalTableProps<T>> {
  static defaultProps = {
    data: [],
    selectedRows: [],
  };
  tableRef: RefObject<VTable> = createRef();

  componentDidMount() {
    if (!this.props.getRowKey) {
      console.error('Warning: "getRowKey" prop is missing');
    }
  }

  handleSelect = ({ selectedRow, isSelected }) => {
    const { selectedRows, onSelect, getRowKey } = this.props;
    const updatedSelectedRows = isSelected
      ? [...selectedRows, selectedRow]
      : selectedRows.filter((row) => {
          return getRowKey
            ? getRowKey(row) !== getRowKey(selectedRow)
            : !Object.is(row, selectedRow);
        });

    onSelect?.(updatedSelectedRows);
  };

  handleSelectAll = (isSelectedAll) => {
    const { data, onSelect, selectedRows: selections, getRowKey } = this.props;

    const removeActiveSelections = (
      selectedRows: any[] = [],
      currentRows: any[] = []
    ) => {
      return selectedRows.filter((row) => {
        return !currentRows.some((currentRow) => {
          if (getRowKey) {
            return getRowKey(row) === getRowKey(currentRow);
          } else {
            return Object.is(row, currentRow);
          }
        });
      });
    };

    const passiveSelections = removeActiveSelections(selections, data);
    const allSelections = [...passiveSelections, ...data];

    const selectedRows = isSelectedAll ? allSelections : passiveSelections;

    onSelect?.(selectedRows);
  };

  handleRowClick = ({ rowData, index }) => {
    const { tableRef } = this;
    const { expandedRows, onExpand } = this.props;

    if (!expandedRows || !onExpand) return;

    const updatedExpandedRows = expandedRows.includes(rowData)
      ? expandedRows.filter((row) => row !== rowData)
      : [...expandedRows, rowData];

    onExpand(updatedExpandedRows);

    tableRef.current?.recomputeRowHeights(index);
  };

  rowGetter = ({ index }) => {
    const { data } = this.props;

    return data[index];
  };

  rowHeight = (indexArg: Index): number => {
    const { index } = indexArg;
    const { data, expandedRowHeight, expandedRows, rowHeight } = this.props;
    const calculatedRowHeight =
      typeof rowHeight === "number" ? rowHeight : rowHeight(indexArg);
    return expandedRows && expandedRows.includes(data[index])
      ? expandedRowHeight + calculatedRowHeight
      : calculatedRowHeight;
  };

  rowRenderer = (props) => {
    const { handleSelect, handleRowClick } = this;
    const {
      expandedRowHeight,
      expandedRows,
      expandableContent,
      getRowKey,
      rowHeight,
      selectedRows,
    } = this.props;
    const { rowData } = props;

    return (
      <RowRenderer
        {...{
          ...props,
          isSelectable: !!selectedRows,
          key: getRowKey ? getRowKey(rowData) : props.key,
          expandedRowHeight,
          expandedRows,
          expandableContent,
          getRowKey,
          onRowClick: handleRowClick,
          onSelect: handleSelect,
          rowHeight,
          selectedRows,
        }}
      />
    );
  };

  headerRowRenderer = (props) => {
    const { handleSelectAll } = this;
    const { selectedRows, data, getRowKey, isSelectedAll } = this.props;

    const isAllRowsSelected = (selectedRows, data) => {
      if (selectedRows.length === data.length) {
        return !selectedRows.some((row, index) => {
          if (getRowKey) {
            return getRowKey(row) !== getRowKey(data[index]);
          } else {
            return !Object.is(row, data[index]);
          }
        });
      }
      return false;
    };

    return (
      <HeaderRowRenderer
        {...{
          ...props,
          isSelectable: !!selectedRows,
          isSelectedAll: isSelectedAll
            ? isSelectedAll
            : selectedRows && isAllRowsSelected(selectedRows, data),
          onSelectAll: handleSelectAll,
        }}
      />
    );
  };

  render() {
    const {
      tableRef,
      headerRowRenderer,
      rowGetter,
      rowHeight,
      rowRenderer,
    } = this;
    const { data, headerHeight, children, virtualizedProps } = this.props;

    return (
      <>
        <GlobalStyle />

        <AutoSizer>
          {({ width, height }) => (
            <VTable
              {...{
                ...virtualizedProps,
                children,
                headerHeight,
                ref: tableRef,
                width,
                height,
                rowHeight,
                rowCount: data.length,
                rowGetter,
                rowRenderer,
                headerRowRenderer,
              }}
            />
          )}
        </AutoSizer>
      </>
    );
  }
}

export { Column, SortDirection };
